import * as React from "react";

const MAX_SELECTION = 50;

export default function useBulkSelection() {
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  const isSelected = React.useCallback(
    (productId) => selectedIds.has(productId),
    [selectedIds],
  );

  const toggle = React.useCallback((productId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else if (next.size < MAX_SELECTION) {
        next.add(productId);
      }
      return next;
    });
  }, []);

  const selectAll = React.useCallback((productIds) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of productIds) {
        if (next.size >= MAX_SELECTION) break;
        next.add(id);
      }
      return next;
    });
  }, []);

  const deselectAll = React.useCallback((productIds) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      productIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectionCount = selectedIds.size;
  const isAtLimit = selectionCount >= MAX_SELECTION;

  return React.useMemo(
    () => ({
      selectedIds,
      isSelected,
      toggle,
      selectAll,
      deselectAll,
      clearSelection,
      selectionCount,
      isAtLimit,
      maxSelection: MAX_SELECTION,
    }),
    [
      selectedIds,
      isSelected,
      toggle,
      selectAll,
      deselectAll,
      clearSelection,
      selectionCount,
      isAtLimit,
    ],
  );
}
